import { useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { useEffect } from "react";

function Receptionist() {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    interest: "Body Building",
  });
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("leads");
  const [leadId, setLeadId] = useState(null);
  
  // Presentation State
  const [presentationMode, setPresentationMode] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // Purchase State
  const [purchaseData, setPurchaseData] = useState({
    plan: "",
    discount: "",
    notes: "",
    paymentStatus: "Paid",
    paymentMethod: "Cash"
  });
  const [termsCompleted, setTermsCompleted] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "timestamp", direction: "desc" });

  // Fullscreen & Scroll Lock Logic
  useEffect(() => {
    const handleFullscreenChange = () => {
      // We don't automatically set presentationMode to false here if fullscreen fails, 
      // because we still want the CSS overlay fallback to work.
      // We only exit if the user explicitly exits fullscreen via system UI.
      if (!document.fullscreenElement && !document.webkitFullscreenElement && presentationMode) {
        // Checking if we deliberately exited or user pressed Esc/back
        // This can be tricky, so let's rely on the Close button primarily
        // but if they exit fullscreen, we close the presentation.
        setPresentationMode(false);
      }
    };
    
    if (presentationMode) {
      document.body.style.overflow = 'hidden'; // Lock background scroll
    } else {
      document.body.style.overflow = 'auto'; // Unlock background scroll
    }

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
      document.body.style.overflow = 'auto';
    };
  }, [presentationMode]);

  const handleStartPresentation = (index = 0) => {
    setPresentationMode(true);
    setCurrentSlide(index);
    const element = document.documentElement;
    try {
      if (element.requestFullscreen) {
        const promise = element.requestFullscreen();
        if (promise && promise.catch) {
          promise.catch(err => console.error(err));
        }
      } else if (element.webkitRequestFullscreen) { /* Safari */
        element.webkitRequestFullscreen();
      } else if (element.msRequestFullscreen) { /* IE11 */
        element.msRequestFullscreen();
      }
    } catch (e) {
      console.error("Fullscreen API error:", e);
    }
  };

  const handleEndPresentation = () => {
    setPresentationMode(false);
    try {
      if (document.fullscreenElement || document.webkitFullscreenElement) {
        if (document.exitFullscreen) {
          const promise = document.exitFullscreen();
          if (promise && promise.catch) {
            promise.catch(err => console.error(err));
          }
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        }
      }
    } catch (e) {
      console.error("Exit fullscreen error:", e);
    }
  };

  const addLead = useMutation("leads:add");
  const updateLead = useMutation("leads:update");
  const posters = useQuery("posters:get") || [];
  const pricing = useQuery("pricing:get") || [];
  const allLeads = useQuery("leads:get") || [];

  useEffect(() => {
    let timer;
    if (presentationMode && currentSlide >= posters.length) {
      timer = setTimeout(() => {
        setPresentationMode(false);
        setActiveTab("purchase");
      }, 10000);
    }
    return () => clearTimeout(timer);
  }, [presentationMode, currentSlide, posters.length]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const id = await addLead({
        ...formData,
        sourceTag: "Visit",
      });
      setLeadId(id);
      setActiveTab("posters"); // Move to posters tab
      setLoading(false);
    } catch (err) {
      console.error("Error adding lead:", err);
      setLoading(false);
    }
  };

  const handlePurchase = async (e) => {
    e.preventDefault();
    if (purchaseData.paymentStatus === "Paid" && !termsCompleted) {
      alert("Please confirm the client has completed the Terms & Conditions Form.");
      return;
    }
    setLoading(true);
    try {
      await updateLead({
        id: leadId,
        status: purchaseData.paymentStatus === "Paid" ? "Converted" : "Enquired",
        purchasedPlan: purchaseData.plan,
        googleFormCompleted: termsCompleted,
        paymentMethod: purchaseData.paymentStatus === "Paid" ? purchaseData.paymentMethod : undefined,
        comment: purchaseData.notes + (purchaseData.discount ? ` (Discount: ${purchaseData.discount})` : "") + ` [Status: ${purchaseData.paymentStatus}]`
      });
      alert(`Status Updated to ${purchaseData.paymentStatus}!`);
      setLeadId(null);
      setActiveTab("leads");
      setPurchaseData({ plan: "", discount: "", notes: "", paymentStatus: "Paid", paymentMethod: "Cash" });
      setTermsCompleted(false);
    } catch (err) {
      console.error("Error updating lead:", err);
    } finally {
      setLoading(false);
    }
  };

  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  const handleTouchStart = (e) => setTouchStart(e.targetTouches[0].clientX);
  const handleTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);
  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) nextSlide();
    if (isRightSwipe) prevSlide();
    
    setTouchStart(0);
    setTouchEnd(0);
  };

  const nextSlide = () => {
    if (currentSlide < posters.length) {
      setCurrentSlide(currentSlide + 1);
    } else {
      // Reached the end
      handleEndPresentation();
      setActiveTab("purchase");
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const exportToCSV = () => {
    const headers = ["Client Name", "Contact", "Email", "Address", "Source", "Interest", "Status", "Plan", "Payment Method", "Form Completed", "Date"];
    const csvData = allLeads.map(lead => [
      lead.name,
      lead.phone,
      lead.email || "N/A",
      lead.address || "N/A",
      lead.sourceTag || "Website",
      lead.interest,
      lead.status || "Lead",
      lead.purchasedPlan || "-",
      lead.paymentMethod || "-",
      lead.googleFormCompleted ? "Yes" : "No",
      new Date(lead.timestamp).toLocaleDateString()
    ]);
    
    const csvContent = [
      headers.join(","),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(","))
    ].join("\n");
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", `leads_export_${new Date().toISOString().slice(0,10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedAndFilteredLeads = [...allLeads]
    .filter(lead => 
      lead.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
      (lead.phone && lead.phone.includes(searchQuery))
    )
    .sort((a, b) => {
      if (!a[sortConfig.key]) return 1;
      if (!b[sortConfig.key]) return -1;
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });

  return (
    <>
      <section className="receptionist-section">
        <div className="container page-padding pt-4 pb-[5rem] md:py-[5rem]">
          <div className="flex flex-col items-center w-full">
            {/* Tabs Navigation */}
            <div className="flex gap-10 mb-10 border-b w-full justify-center overflow-x-auto whitespace-nowrap px-4">
              <button 
                onClick={() => setActiveTab("leads")}
                className={`text-[1.8rem] font-bold pb-4 px-8 ${activeTab === "leads" ? "text-[#ff0336] border-b-4 border-[#ff0336]" : "text-gray-500"}`}
              >
                Leads Dashboard
              </button>
              <button 
                onClick={() => setActiveTab("capture")}
                className={`text-[1.8rem] font-bold pb-4 px-8 ${activeTab === "capture" ? "text-[#ff0336] border-b-4 border-[#ff0336]" : "text-gray-500"}`}
              >
                Capture Client
              </button>
              <button 
                onClick={() => setActiveTab("posters")}
                className={`text-[1.8rem] font-bold pb-4 px-8 ${activeTab === "posters" ? "text-[#ff0336] border-b-4 border-[#ff0336]" : "text-gray-500"}`}
              >
                Presentation
              </button>
              <button 
                onClick={() => setActiveTab("purchase")}
                className={`text-[1.8rem] font-bold pb-4 px-8 ${activeTab === "purchase" ? "text-[#ff0336] border-b-4 border-[#ff0336]" : "text-gray-500"}`}
              >
                Conversion
              </button>
            </div>

            {/* Leads Dashboard Tab */}
            {activeTab === "leads" && (
              <div className="w-full animate-fadeIn overflow-x-auto">
                <div className="flex justify-between items-center mb-10 flex-wrap gap-4">
                  <h2 className="text-[3rem] font-bold uppercase">Client Enquiries</h2>
                  <div className="flex gap-4">
                    <input 
                      type="text" 
                      placeholder="Search by name or phone..." 
                      className="px-4 py-3 border border-gray-300 rounded-md text-[1.4rem] outline-none"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <button onClick={exportToCSV} className="bg-green-600 text-white px-8 py-3 text-[1.4rem] font-bold uppercase hover:bg-green-700 transition-all">Export to Sheets</button>
                    <button onClick={() => setActiveTab("capture")} className="bg-black text-white px-8 py-3 text-[1.4rem] font-bold uppercase hover:bg-[#ff0336] transition-all">Add New Lead</button>
                  </div>
                </div>
                <table className="w-full text-left border-collapse bg-white shadow-lg rounded-xl overflow-hidden">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="py-6 px-8 text-[1.6rem] font-bold cursor-pointer hover:bg-gray-200" onClick={() => handleSort('name')}>Client Name ↕</th>
                      <th className="py-6 px-8 text-[1.6rem] font-bold cursor-pointer hover:bg-gray-200" onClick={() => handleSort('phone')}>Contact ↕</th>
                      <th className="py-6 px-8 text-[1.6rem] font-bold cursor-pointer hover:bg-gray-200" onClick={() => handleSort('status')}>Status ↕</th>
                      <th className="py-6 px-8 text-[1.6rem] font-bold">Plan (Method)</th>
                      <th className="py-6 px-8 text-[1.6rem] font-bold">Form Completed</th>
                      <th className="py-6 px-8 text-[1.6rem] font-bold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedAndFilteredLeads.map((lead) => (
                      <tr key={lead._id} className="border-b hover:bg-gray-50 transition-colors">
                        <td className="py-6 px-8 text-[1.6rem] font-medium">{lead.name}</td>
                        <td className="py-6 px-8">
                          <a href={`tel:${lead.phone}`} className="text-[1.6rem] text-[#ff0336] font-bold hover:underline">
                            {lead.phone}
                          </a>
                        </td>
                        <td className="py-6 px-8">
                          <span className={`px-4 py-1 rounded-full text-[1.2rem] font-bold uppercase ${
                            lead.status === "Converted" ? "bg-green-100 text-green-700" : 
                            lead.status === "Enquired" ? "bg-blue-100 text-blue-700" :
                            "bg-yellow-100 text-yellow-700"
                          }`}>
                            {lead.status || "Lead"}
                          </span>
                        </td>
                        <td className="py-6 px-8 text-[1.6rem] font-bold">{lead.purchasedPlan ? `${lead.purchasedPlan} (${lead.paymentMethod || 'Cash'})` : "-"}</td>
                        <td className="py-6 px-8 text-[1.6rem] font-bold">
                          {lead.googleFormCompleted ? (
                            <span className="text-green-600 flex items-center gap-2"><i className="fa-solid fa-check"></i> Yes</span>
                          ) : lead.status === "Converted" ? (
                            <button 
                              onClick={() => { 
                                setLeadId(lead._id); 
                                setPurchaseData({
                                  plan: lead.purchasedPlan || "",
                                  discount: "",
                                  notes: lead.comment || "",
                                  paymentStatus: "Paid",
                                  paymentMethod: lead.paymentMethod || "Cash"
                                });
                                setActiveTab("purchase"); 
                              }}
                              className="bg-yellow-500 text-white px-4 py-2 text-[1.2rem] font-bold uppercase rounded hover:bg-yellow-600 transition-colors"
                            >
                              + Add Form
                            </button>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="py-6 px-8">
                          <button 
                            onClick={() => { setLeadId(lead._id); setActiveTab("posters"); }}
                            className="bg-black text-white px-4 py-2 text-[1.2rem] font-bold uppercase hover:bg-[#ff0336]"
                          >
                            Present
                          </button>
                        </td>
                      </tr>
                    ))}
                    {sortedAndFilteredLeads.length === 0 && <tr><td colSpan="6" className="py-20 text-center text-[1.8rem] text-gray-500">No leads found.</td></tr>}
                  </tbody>
                </table>
              </div>
            )}

            {/* Capture Client Tab */}
            {activeTab === "capture" && (
              <div className="flex flex-col items-center w-full animate-fadeIn">
                <h2 className="text-[3rem] font-bold mb-10 uppercase text-center">Capture Client Enquiry</h2>
                <form
                  onSubmit={handleSubmit}
                  className="flex flex-col py-20 px-10 bg-[#f8f8f8] w-[60rem] min450:w-full shadow-lg"
                >
                  <input
                    className="text-[1.6rem] px-8 py-4 mb-8 outline-none border border-[#e4e4e4]"
                    placeholder="Client Name *"
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                  <input
                    className="text-[1.6rem] px-8 py-4 mb-8 outline-none border border-[#e4e4e4]"
                    placeholder="Phone Number *"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                  <input
                    className="text-[1.6rem] px-8 py-4 mb-8 outline-none border border-[#e4e4e4]"
                    placeholder="Email Address"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                  <input
                    className="text-[1.6rem] px-8 py-4 mb-8 outline-none border border-[#e4e4e4]"
                    placeholder="Address"
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  />
                  <select
                    className="text-[1.6rem] px-8 py-4 mb-10 outline-none border border-[#e4e4e4] bg-white"
                    value={formData.interest}
                    onChange={(e) => setFormData({ ...formData, interest: e.target.value })}
                  >
                    <option>Body Building</option>
                    <option>Boxing</option>
                    <option>Crossfit</option>
                    <option>Fitness</option>
                    <option>Yoga</option>
                  </select>

                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-[#ff0336] text-white text-[1.8rem] font-bold py-5 hover:bg-[#ff0336ce] transition-all"
                  >
                    {loading ? "Saving..." : "Show Pricing Plans"}
                  </button>
                </form>
              </div>
            )}

            {/* Success Message Tab (Hidden from nav) */}
            {activeTab === "enquiry" && (
              <div className="flex flex-col items-center animate-fadeIn w-full">
                <div className="bg-green-100 text-green-700 p-6 text-[1.8rem] rounded-lg mb-10 font-bold">
                  ✓ Enquiry Captured Successfully!
                </div>
                <div className="bg-[#f8f8f8] p-10 rounded-lg shadow-md w-[50rem] text-[1.6rem]">
                  <p className="mb-4"><strong>Name:</strong> {formData.name}</p>
                  <p className="mb-4"><strong>Phone:</strong> {formData.phone}</p>
                  <p className="mb-4"><strong>Email:</strong> {formData.email || "N/A"}</p>
                  <p className="mb-4"><strong>Address:</strong> {formData.address || "N/A"}</p>
                  <p className="mb-4"><strong>Interest:</strong> {formData.interest}</p>
                </div>
                <button 
                  onClick={() => setActiveTab("capture")}
                  className="mt-10 text-[1.6rem] font-bold text-[#ff0336] underline"
                >
                  New Enquiry
                </button>
              </div>
            )}

            {/* Presentation Tab */}
            {activeTab === "posters" && (
              <div className="w-full animate-fadeIn flex flex-col items-center">
                <div className="flex justify-between items-center w-full mb-10">
                   <h2 className="text-[3rem] font-bold uppercase">Current Offers & Graphics</h2>
                   {leadId && <span className="text-[1.4rem] font-bold bg-green-100 text-green-700 px-4 py-2 rounded">Active Lead: {allLeads.find(l => l._id === leadId)?.name}</span>}
                </div>
                
                <button 
                  onClick={() => handleStartPresentation(0)}
                  className="bg-black text-white px-10 py-5 text-[1.8rem] font-bold mb-12 hover:bg-[#ff0336] transition-all uppercase"
                >
                  ▶ Start Presentation
                </button>

                <div className="grid grid-cols-3 gap-10 md1000:grid-cols-1">
                  {posters.map((poster, index) => (
                    <div key={poster._id} className="overflow-hidden rounded-xl shadow-lg hover:scale-105 transition-transform cursor-pointer" onClick={() => handleStartPresentation(index)}>
                      <img src={poster.url} alt="poster" className="w-full h-[40rem] object-cover" />
                    </div>
                  ))}
                </div>
                {posters.length === 0 && <p className="text-[1.8rem] text-center text-gray-500 mt-10">No posters available at the moment.</p>}
              </div>
            )}

            {/* Purchase/Conversion Tab */}
            {activeTab === "purchase" && (
               <div className="w-full max-w-[60rem] animate-fadeIn">
                  <h2 className="text-[3rem] font-bold mb-10 uppercase text-center">Finalize Purchase</h2>
                  {!leadId && <p className="text-center text-[1.6rem] text-red-500 mb-6 font-bold">Note: No active lead selected. Please select a lead from the dashboard to convert.</p>}
                  <form onSubmit={handlePurchase} className="bg-[#f8f8f8] p-12 rounded-xl shadow-lg flex flex-col gap-8">
                    <div className="flex flex-col gap-2">
                      <label className="text-[1.6rem] font-bold">Select Plan *</label>
                      <select 
                        required
                        className="text-[1.6rem] px-6 py-4 border outline-none bg-white"
                        value={purchaseData.plan}
                        onChange={(e) => setPurchaseData({ ...purchaseData, plan: e.target.value })}
                      >
                        <option value="">Select a plan</option>
                        {pricing.map(plan => (
                          <option key={plan._id} value={plan.title}>{plan.title} - ₹{plan.price}</option>
                        ))}
                      </select>
                    </div>

                    <div className="flex flex-col gap-2">
                      <label className="text-[1.6rem] font-bold">Apply Offer/Discount</label>
                      <input 
                        className="text-[1.6rem] px-6 py-4 border outline-none"
                        placeholder="e.g. SUMMER50"
                        value={purchaseData.discount}
                        onChange={(e) => setPurchaseData({ ...purchaseData, discount: e.target.value })}
                      />
                    </div>

                    <div className="flex flex-col gap-2">
                      <label className="text-[1.6rem] font-bold">Payment / Lead Status *</label>
                      <div className="flex gap-4">
                        {["Paid", "Enquired"].map(status => (
                          <button
                            key={status}
                            type="button"
                            onClick={() => setPurchaseData({ ...purchaseData, paymentStatus: status })}
                            className={`flex-1 py-4 text-[1.6rem] font-bold rounded-lg border-2 transition-all ${purchaseData.paymentStatus === status ? "bg-[#ff0336] text-white border-[#ff0336]" : "bg-white text-gray-500 border-gray-200"}`}
                          >
                            {status}
                          </button>
                        ))}
                      </div>
                    </div>

                    {purchaseData.paymentStatus === "Paid" && (
                      <div className="flex flex-col gap-2">
                        <label className="text-[1.6rem] font-bold">Payment Method *</label>
                        <div className="flex gap-4">
                          {["Cash", "Card", "UPI"].map(method => (
                            <button
                              key={method}
                              type="button"
                              onClick={() => setPurchaseData({ ...purchaseData, paymentMethod: method })}
                              className={`flex-1 py-4 text-[1.6rem] font-bold rounded-lg border-2 flex items-center justify-center gap-2 transition-all ${purchaseData.paymentMethod === method ? "bg-black text-white border-black" : "bg-white text-gray-500 border-gray-200"}`}
                            >
                              <i className={`fa-solid ${method === "Cash" ? "fa-money-bill" : method === "Card" ? "fa-credit-card" : "fa-qrcode"}`}></i> {method}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {purchaseData.paymentStatus === "Paid" && (
                      <div className="flex flex-col items-center gap-4 p-6 bg-white border border-gray-200 rounded-lg shadow-sm">
                        <label className="text-[1.6rem] font-bold text-center uppercase text-[#ff0336]">Terms & Conditions Agreement</label>
                        <p className="text-[1.4rem] text-gray-500 text-center mb-2">Please ask the client to scan this QR code and fill out the agreement form before proceeding.</p>
                        <img 
                          src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=https://docs.google.com/forms/d/e/1FAIpQLScPjqpPEGxBBcZBtr_nZ3uazDVJzzVpYXAxBxbmVsHdXw_5dg/viewform?usp=publish-editor" 
                          alt="Google Form QR Code" 
                          className="w-[150px] h-[150px] border p-2 rounded shadow-sm"
                        />
                        <label className="flex items-center gap-3 mt-4 cursor-pointer bg-gray-50 p-4 rounded-lg w-full justify-center border border-gray-200 hover:bg-gray-100 transition-colors">
                          <input 
                            type="checkbox" 
                            className="w-8 h-8 accent-[#ff0336] cursor-pointer"
                            checked={termsCompleted}
                            onChange={(e) => setTermsCompleted(e.target.checked)}
                          />
                          <span className="text-[1.6rem] font-bold">Client has completed Terms & Conditions Form</span>
                        </label>
                      </div>
                    )}

                    <div className="flex flex-col gap-2">
                      <label className="text-[1.6rem] font-bold">Additional Notes</label>
                      <textarea 
                        className="text-[1.6rem] px-6 py-4 border outline-none h-32"
                        placeholder="Special requests or payment notes..."
                        value={purchaseData.notes}
                        onChange={(e) => setPurchaseData({ ...purchaseData, notes: e.target.value })}
                      />
                    </div>

                    <button 
                      type="submit"
                      disabled={loading || !leadId || (purchaseData.paymentStatus === "Paid" && !termsCompleted)}
                      className="bg-[#ff0336] text-white py-5 text-[2rem] font-bold uppercase hover:bg-black transition-all disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {loading ? "Processing..." : "Confirm & Convert Lead"}
                    </button>
                  </form>
               </div>
            )}
          </div>
        </div>
      </section>

      {/* Presentation Overlay */}
      {presentationMode && (
        <div 
          className="fixed inset-0 bg-black z-[999999] flex flex-col items-center justify-center animate-fadeIn touch-none"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Close Button - Increased Z-Index */}
          <button 
            onClick={handleEndPresentation}
            className="absolute top-10 right-10 text-white text-[1.4rem] font-bold bg-[#ff0336] px-6 py-3 rounded-full hover:bg-white hover:text-black transition-all uppercase z-[1000001]"
          >
            End Presentation Early
          </button>

          {/* Slides Container */}
          <div className="w-full h-full flex items-center justify-center relative px-4 md:px-20">
             {currentSlide < posters.length ? (
               <div className="relative w-full max-w-[100rem] h-[90vh] md:h-[80vh] animate-slideIn">
                  <img 
                    src={posters[currentSlide].url} 
                    alt="Slide" 
                    className="w-full h-full object-contain shadow-2xl"
                  />
                  
                  {/* Navigation Arrows */}
                  <button 
                    onClick={prevSlide}
                    className={`absolute left-0 md:left-[-10rem] top-1/2 -translate-y-1/2 text-white text-[5rem] hover:text-[#ff0336] transition-colors bg-black/20 md:bg-transparent px-4 py-8 rounded-r-xl md:rounded-none ${currentSlide === 0 ? "opacity-30 cursor-not-allowed" : ""}`}
                  >
                    ❮
                  </button>
                  <button 
                    onClick={nextSlide}
                    className="absolute right-0 md:right-[-10rem] top-1/2 -translate-y-1/2 text-white text-[5rem] hover:text-[#ff0336] transition-colors bg-black/20 md:bg-transparent px-4 py-8 rounded-l-xl md:rounded-none"
                  >
                    ❯
                  </button>

                  {/* Progress Indicator */}
                  <div className="absolute bottom-[-5rem] left-1/2 -translate-x-1/2 flex gap-4">
                    {posters.map((_, idx) => (
                      <span key={idx} className={`w-4 h-4 rounded-full ${idx === currentSlide ? "bg-[#ff0336]" : "bg-gray-600"}`}></span>
                    ))}
                    <span className={`w-4 h-4 rounded-full ${currentSlide === posters.length ? "bg-[#ff0336]" : "bg-gray-600"}`}></span>
                  </div>
               </div>
             ) : (
               /* Final CTA Slide */
               <div className="flex flex-col items-center text-white text-center animate-fadeIn">
                  <h2 className="text-[6rem] font-bold mb-4 uppercase">Let's get you started</h2>
                  <p className="text-[2.4rem] text-gray-400 mb-12">Take the first step towards your fitness goals today!</p>
                  <div className="flex gap-8">
                    <button 
                      onClick={() => {
                        handleEndPresentation();
                        setActiveTab("purchase");
                      }}
                      className="bg-[#ff0336] text-white px-12 py-6 text-[2.2rem] font-bold uppercase hover:scale-105 transition-all"
                    >
                      Proceed to Purchase
                    </button>
                    <button 
                      onClick={() => {
                        setCurrentSlide(0);
                      }}
                      className="bg-white text-black px-12 py-6 text-[2.2rem] font-bold uppercase hover:bg-gray-200 transition-all"
                    >
                      Restart Presentation
                    </button>
                  </div>
                  
                  <p className="mt-12 text-[1.4rem] text-gray-500 italic">Auto-redirecting to purchase page in 10 seconds...</p>
               </div>
             )}
          </div>
          
          {/* Click to advance hint */}
          {currentSlide < posters.length && (
            <div 
              className="absolute inset-0 z-[-1] cursor-pointer" 
              onClick={nextSlide}
            ></div>
          )}
        </div>
      )}
    </>
  );
}

export default Receptionist;
